/**
 * SalesAllocationEditSameDoDialog — bulk "Edit (similar DO)" grid for Sales Control ›
 * Sale Allocation. Converted 1:1 from Access `frmCtrlSalesQrySaleAlc_Edit_SameDO`
 * (wrapper) + subform `frmCtrlSalesQrySaleAlc_TickCont` (datasheet grid).
 *
 * Behaviour (Access btnEditMany_Click → form open):
 *   - Open with `doNo` = the selected list row's delivery_order. Empty doNo →
 *     parent must warn "Do no is empty!" before opening; this dialog also guards.
 *   - Load every allocation that shares that delivery_order into a grid, each row
 *     pre-ticked (checked_row = 1).
 *   - "Tick All" header checkbox toggles every row (Access tick_all_Click).
 *   - Only three columns are editable per row (Access labels carry "(EDIT)"):
 *       Receipt No · Alc Bag · Alc Quantity  (UPDATE target columns).
 *   - btnSave loops the TICKED rows and UPDATEs receipt_no / allocating_bag /
 *     allocating_quantity for each (is_status = 0). Unticked rows are skipped.
 *
 * The other columns (Container No, M. Date, M. Truck No, M. Net weight, Contract No,
 * DO, Booking No., Contract Quality, From WH) are display-only context — read-only
 * in the grid exactly like the Access datasheet (no Enabled/Locked override there).
 *
 * BE NOT BUILT YET: hooks come from `saleAllocationEditSameDoApi` (GET same-do /
 * PUT same-do, both TODO). Until shipped the grid shows an empty state, no fake rows.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogContent,
  DialogTitle, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'

import {
  type SaleAllocationSameDoRow,
  type SaleAllocationSameDoUpdateItem,
  useGetSaleAllocationsSameDoQuery,
  useUpdateSaleAllocationsSameDoMutation,
} from '../../store/api/saleAllocationEditSameDoApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import { DISPLAY_DATE_FORMAT } from '../../utils/date'
import dayjs from 'dayjs'

interface Props {
  open: boolean
  /** The selected list row's delivery_order — every allocation sharing it is loaded. */
  doNo: string
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

/** Per-row editable draft + tick state, keyed by salesAllocationScsId. */
interface RowDraft {
  checked: boolean
  receiptNo: string
  allocatingBag: string
  allocatingQuantity: string
}

const SM = 'small' as const
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const headCellSx: SxProps<Theme> = { fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap', py: 0.75 }
const bodyCellSx: SxProps<Theme> = { fontSize: '0.75rem', whiteSpace: 'nowrap', py: 0.4 }
const editFieldSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 30, fontSize: '0.75rem' },
  '& .MuiInputBase-input': { py: 0.4, fontSize: '0.75rem' },
}
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }

function parseNum(v: string): number | null {
  const t = (v ?? '').toString().replace(/,/g, '').trim()
  if (!t) return null
  const n = parseFloat(t)
  return Number.isFinite(n) ? n : null
}
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const s = (v: unknown): string => (v == null ? '' : String(v))
const fmtDate = (v: string | null): string => (v ? dayjs(v).format(DISPLAY_DATE_FORMAT) : '')

export default function SalesAllocationEditSameDoDialog({ open, doNo, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [drafts, setDrafts] = useState<Record<number, RowDraft>>({})

  const { data, isFetching, isError } = useGetSaleAllocationsSameDoQuery(doNo, { skip: !open || !doNo })
  const rows: SaleAllocationSameDoRow[] = useMemo(() => data?.rows ?? [], [data])

  const [updateRows, { isLoading: saving }] = useUpdateSaleAllocationsSameDoMutation()

  // Hydrate drafts when rows arrive. Access pre-ticks every row (checked_row = 1)
  // and pre-formats the three editable number/text fields to Standard.
  useEffect(() => {
    if (!open) return
    const next: Record<number, RowDraft> = {}
    for (const r of rows) {
      next[r.salesAllocationScsId] = {
        checked: true,
        receiptNo: s(r.receiptNo),
        allocatingBag: fmtStd(r.allocatingBag),
        allocatingQuantity: fmtStd(r.allocatingQuantity),
      }
    }
    setDrafts(next)
  }, [open, rows])

  const setRow = (id: number, patch: Partial<RowDraft>) =>
    setDrafts((p) => ({ ...p, [id]: { ...p[id], ...patch } }))

  const allChecked = rows.length > 0 && rows.every((r) => drafts[r.salesAllocationScsId]?.checked)
  const someChecked = rows.some((r) => drafts[r.salesAllocationScsId]?.checked)
  const checkedCount = rows.filter((r) => drafts[r.salesAllocationScsId]?.checked).length

  // Access tick_all_Click — toggle every row on/off.
  const toggleAll = (checked: boolean) =>
    setDrafts((p) => {
      const next = { ...p }
      for (const r of rows) next[r.salesAllocationScsId] = { ...next[r.salesAllocationScsId], checked }
      return next
    })

  const canSave = canEdit && !saving && checkedCount > 0

  const handleClose = () => { if (!saving) onClose() }

  // Access btnSave_Click — UPDATE each ticked row's three editable columns.
  const handleSave = async () => {
    if (!canSave) return
    const payload: SaleAllocationSameDoUpdateItem[] = rows
      .filter((r) => drafts[r.salesAllocationScsId]?.checked)
      .map((r) => {
        const d = drafts[r.salesAllocationScsId]
        return {
          salesAllocationScsId: r.salesAllocationScsId,
          receiptNo: d.receiptNo.trim() || null,
          allocatingBag: parseNum(d.allocatingBag),
          allocatingQuantity: parseNum(d.allocatingQuantity),
        }
      })
    try {
      await updateRows({ rows: payload }).unwrap()
      snack.success('Successfully updated')
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Update failed'))
    }
  }

  // Display-only context columns (TickCont datasheet order by Top).
  const displayCols: { key: keyof SaleAllocationSameDoRow; label: string; num?: boolean; date?: boolean }[] = [
    { key: 'containerNo', label: 'Container No.' },
    { key: 'mDate', label: 'M. Date', date: true },
    { key: 'mTruckNo', label: 'M. Truck No' },
    { key: 'mNetWeight', label: 'M. Net weight', num: true },
    { key: 'saleContract', label: 'Contract No' },
    { key: 'deliveryOrder', label: 'DO' },
    { key: 'bookingNo', label: 'Booking No.' },
    { key: 'contractQuality', label: 'Contract Quality' },
    { key: 'fromWarehouse', label: 'From WH' },
  ]

  return (
    <Dialog
      open={open}
      maxWidth="xl"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>
          Edit (similar DO){doNo ? ` — ${doNo}` : ''}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>(Only edit ticked rows)</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit allocations.</Alert>}
        {!doNo && <Alert severity="warning" sx={{ mb: 1.5 }}>Do no is empty!</Alert>}
        {isError && <Alert severity="error" sx={{ mb: 1.5 }}>Could not load allocations for this DO.</Alert>}

        {isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>
            No allocations found for this delivery order.
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table
              size="small"
              stickyHeader
              sx={{
                // Header sticky opaque (theme MuiTableHead = gold 0.18 trong suốt →
                // scroll lòi data); gradient cream-gold qua selector thắng specificity.
                '& thead .MuiTableCell-head': {
                  backgroundColor: '#fff9f4',
                  backgroundImage: 'linear-gradient(180deg, #fff9f4 0%, #f4df9c 100%)',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={headCellSx}>
                    <Stack direction="row" alignItems="center" spacing={0.25}>
                      <Checkbox
                        size={SM}
                        checked={allChecked}
                        indeterminate={!allChecked && someChecked}
                        onChange={(e) => toggleAll(e.target.checked)}
                        disabled={!canEdit}
                      />
                      <span>Tick All</span>
                    </Stack>
                  </TableCell>
                  <TableCell sx={headCellSx}>Receipt No. (EDIT)</TableCell>
                  <TableCell sx={headCellSx} align="right">Alc Bag (EDIT)</TableCell>
                  <TableCell sx={headCellSx} align="right">Alc Quantity (EDIT)</TableCell>
                  {displayCols.map((c) => (
                    <TableCell key={c.key} sx={headCellSx} align={c.num ? 'right' : 'left'}>{c.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const d = drafts[r.salesAllocationScsId]
                  if (!d) return null
                  return (
                    <TableRow key={r.salesAllocationScsId} hover selected={d.checked}>
                      <TableCell padding="checkbox" sx={bodyCellSx}>
                        <Checkbox
                          size={SM}
                          checked={d.checked}
                          onChange={(e) => setRow(r.salesAllocationScsId, { checked: e.target.checked })}
                          disabled={!canEdit}
                        />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <TextField
                          size={SM} fullWidth value={d.receiptNo} disabled={!canEdit} sx={editFieldSx}
                          onChange={(e) => setRow(r.salesAllocationScsId, { receiptNo: e.target.value })}
                        />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <TextField
                          size={SM} fullWidth type="text" value={d.allocatingBag} disabled={!canEdit}
                          sx={editFieldSx} inputProps={numProps}
                          onChange={(e) => setRow(r.salesAllocationScsId, { allocatingBag: e.target.value })}
                          onBlur={() => setRow(r.salesAllocationScsId, { allocatingBag: fmtStd(d.allocatingBag) })}
                        />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <TextField
                          size={SM} fullWidth type="text" value={d.allocatingQuantity} disabled={!canEdit}
                          sx={editFieldSx} inputProps={numProps}
                          onChange={(e) => setRow(r.salesAllocationScsId, { allocatingQuantity: e.target.value })}
                          onBlur={() => setRow(r.salesAllocationScsId, { allocatingQuantity: fmtStd(d.allocatingQuantity) })}
                        />
                      </TableCell>
                      {displayCols.map((c) => {
                        const raw = r[c.key]
                        const text = c.date ? fmtDate(raw as string | null) : c.num ? fmtStd(raw) : s(raw)
                        return (
                          <TableCell key={c.key} sx={bodyCellSx} align={c.num ? 'right' : 'left'}>{text}</TableCell>
                        )
                      })}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  )
}
